import { useCallback, useEffect, useState } from "react";

export type RoadmapViewMode = "solo" | "team";

/**
 * Single source of truth for the Roadmap Builder's Solo/Team view mode.
 *
 * - Default: "solo".
 * - Persisted per-user, per-session in localStorage (key includes userId so
 *   dev impersonation in one browser doesn't leak another user's choice).
 * - When `canShowToggle` is false (State 1: single creator, no invite sent),
 *   the toggle is hidden and the effective mode is forced to "solo".
 */
export function useRoadmapViewMode(
  sessionId: string,
  userId: string,
  canShowToggle: boolean,
): {
  viewMode: RoadmapViewMode;
  setViewMode: (v: RoadmapViewMode) => void;
  showViewToggle: boolean;
} {
  const storageKey = `rb-view-mode:${sessionId}:${userId}`;

  const [stored, setStored] = useState<RoadmapViewMode>(() => {
    try {
      const v = window.localStorage.getItem(storageKey);
      return v === "team" ? "team" : "solo";
    } catch {
      return "solo";
    }
  });

  // Re-hydrate when the session or user changes (e.g. impersonation reload).
  useEffect(() => {
    try {
      const v = window.localStorage.getItem(storageKey);
      setStored(v === "team" ? "team" : "solo");
    } catch {
      setStored("solo");
    }
  }, [storageKey]);

  const setViewMode = useCallback(
    (v: RoadmapViewMode) => {
      setStored(v);
      try {
        window.localStorage.setItem(storageKey, v);
      } catch {
        /* ignore quota/availability errors */
      }
    },
    [storageKey],
  );

  // State 1 has no team to view — force solo and hide the control.
  const viewMode: RoadmapViewMode = canShowToggle ? stored : "solo";

  return { viewMode, setViewMode, showViewToggle: canShowToggle };
}
